import { useCallback } from 'react';
import { useBoolean } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';

import { getName } from '@pletly/shared';
import { OnboardingContainer, UserAvatar } from '@pletly/ui';
import { useAuthHeaders } from '@pletly/feature-auth';

import { RootStackScreenProps } from '../../../../navigation/types';
import { useOrganisationMemberQuery } from '../../components/__generated__/organisationMember.generated';
import { AssignCaregiverModal } from '../../../view/components/AssignCaregiverModal';
import { withTranslation } from '../../../../locales/hoc';

export const FillInformationConfirmation = withTranslation(
	({
		navigation: { replace },
		route: {
			params: { memberId, organisationUuid },
		},
	}: Props) => {
		const { t } = useTranslation();
		const { data } = useOrganisationMemberQuery({ variables: { memberId } });
		const { value: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBoolean();

		const { getHeaders } = useAuthHeaders();

		const handleSkipPress = useCallback(
			() => replace('Home', { uuid: organisationUuid }),
			[replace, organisationUuid],
		);

		if (!data) {
			return null;
		}
		const {
			organisationMember: { id, user },
		} = data;
		const fullNameOrUsername = getName(user);
		const careGiversCount = user.superPals.length;

		const photoSource = user.photoUrl
			? { uri: user.photoUrl, headers: getHeaders(user.photoUrl) }
			: undefined;

		return (
			<>
				<OnboardingContainer
					heroImage={<UserAvatar username={fullNameOrUsername} source={photoSource} size={80} />}
					title={t('fill-information-confirmation.title')}
					subtitle={t('fill-information-confirmation.subtitle', { name: fullNameOrUsername })}
					ctaText={`${t('fill-information-confirmation.ctaButton')} (${careGiversCount})`}
					onNextClick={openModal}
					onSkipClick={handleSkipPress}
					itemsAlignment="center"
				/>
				<AssignCaregiverModal
					visible={isModalOpen}
					onClose={closeModal}
					organisationUuid={organisationUuid}
					careRecipientMember={{
						memberId: id,
						...user,
					}}
				/>
			</>
		);
	},
);

export type FillCareRecipientInformationConfirmationParams = {
	organisationUuid: string;
	memberId: string;
};

type Props = RootStackScreenProps<'FillCareRecipientInformationConfirmation'>;
